import React, { useEffect, useRef, useState } from 'react';
import {
  Button,
  EnterYourComment,
  Avatar,
  Comment as Reply,
  Dialog
} from 'components';
import styled, { css } from 'styled-components';
import { Markup } from 'interweave';
import { PencilIcon, SpeechBubbleReply, TrashIcon } from 'assets/svgs';
import { formatTimestamp, scrollToAnchor, clearQueryParams } from 'utils';
import DeleteCommentDialog from './DeleteCommentDialog';

/* Note how down the end of <Container /> we have a <Reply /> component, which is just a renamed
<Comment /> component (see the import at the top of the file). This time, instead of passing in null
as it's parent, we pass in the current comment.id, so we're effectively recursively rendering <Comment />
components to create the comment tree.  */
const Comment = React.forwardRef(
  (
    {
      i,
      arr,
      parentCommentId,
      level,
      comment,
      comments,
      commentBeingEdited,
      idOfCommentBeingRepliedTo,
      commentIdQueryParam,
      setQuestionIdQueryParam,
      handleEditComment,
      postingOrEditingComment,
      setCommentBeingEdited,
      users,
      user,
      handlePostComment,
      setIdOfCommentBeingRepliedTo,
      handleDeleteComment,
      sessionId,
      deletingComment,
      handleDialog,
      onlineUsers
    },
    ref
  ) => {
    const userWantsToEditThisComment = commentBeingEdited?.id === comment.id;
    const userWantsToReplyToThisComment =
      idOfCommentBeingRepliedTo === comment.id;
    const timestamp = formatTimestamp(comment.timestamp);
    const [showDeleteDialog, setShowDeleteCommentDialog] = useState(false);

    const hasReplies = comments.some((c) => c.parentCommentId === comment.id);
    const commentRef = useRef(null);
    const lastReplyRef = useRef(null);
    const replyRefs = useRef([]);
    const deleteCommentDialogRef = useRef(null);

    useEffect(() => {
      const scrollToThisComment = commentIdQueryParam
        ? arr.some((_comment) => _comment.id === commentIdQueryParam)
        : false;

      if (scrollToThisComment) {
        scrollToAnchor({
          anchor: commentIdQueryParam,
          offset: -64,
          duration: 100
        });
        setQuestionIdQueryParam(null);
        clearQueryParams();
      }
    }, []);

    const handleDismissDeleteCommentDialog = (e) => {
      e.stopPropagation();
      handleDialog({
        dialogRef: deleteCommentDialogRef,
        animation: 'dismiss',
        stateHandler: () => setShowDeleteCommentDialog(false)
      });
    };

    return (
      <Container
        ref={(el) => {
          commentRef.current = el;
          if (ref) {
            if (typeof ref === 'function') ref(el);
            else ref.current = el;
          }
        }}
        id={comment.id}
        key={comment.id}
        $userWantsToEditThisComment={userWantsToEditThisComment}
        $lastComment={i === arr.length - 1}
        $isReply={parentCommentId}
        $level={level}
        $idOfCommentBeingRepliedTo={idOfCommentBeingRepliedTo}
        $isBeingRepliedTo={idOfCommentBeingRepliedTo === comment.id}>
        <TopLine>
          <Avatar
            avatarUrl={comment.avatarUrl}
            alt={comment.name}
            showConnector={hasReplies}
            commentRef={commentRef}
            lastReplyRef={lastReplyRef}
            replyRefs={replyRefs}
            level={level}
            isOnline={onlineUsers?.some((u) => u.id === comment.uid)}
          />
          <h4>
            {comment.name}
            {users
              ?.find((u) => u.id === comment.uid)
              ?.isModerator?.includes(sessionId) && (
              <>
                <span>&nbsp;&nbsp;|&nbsp;&nbsp;</span>
                <IsModerator>Moderator</IsModerator>
              </>
            )}
            {timestamp && (
              <>
                <span>&nbsp;&nbsp;|&nbsp;&nbsp;</span>
                <Timestamp>{timestamp}</Timestamp>
              </>
            )}
          </h4>
        </TopLine>
        <div style={{ display: userWantsToEditThisComment ? 'none' : 'block' }}>
          <Markup content={comment.html} tagName="div" className="markup" />
          <Buttons>
            {comment.uid === user.uid && (
              <Button
                $variant="text"
                textColor="var(--blue)"
                icon={<PencilIcon width="16px" height="15px" />}
                onClick={() => {
                  if (userWantsToReplyToThisComment) {
                    setIdOfCommentBeingRepliedTo(null);
                  }
                  setCommentBeingEdited(comment);
                }}>
                Edit
              </Button>
            )}
            <Button
              $variant="text"
              textColor="var(--blue)"
              icon={<SpeechBubbleReply width="17px" height="15px" />}
              onClick={() => {
                if (userWantsToEditThisComment) {
                  setCommentBeingEdited(null);
                }
                setIdOfCommentBeingRepliedTo(comment.id);
              }}>
              Reply
            </Button>
            {user.isModerator?.includes(sessionId) && (
              <Button
                $variant="text"
                textColor="var(--blue)"
                icon={
                  <TrashIcon
                    width="15px"
                    style={{ position: 'relative', bottom: '1px' }}
                  />
                }
                onClick={() => setShowDeleteCommentDialog(true)}>
                Delete
              </Button>
            )}
          </Buttons>
        </div>
        <div style={{ display: userWantsToEditThisComment ? 'block' : 'none' }}>
          <EnterYourComment
            key={comment.id}
            handleEditComment={handleEditComment}
            commentBeingEdited={commentBeingEdited}
            postingOrEditingComment={postingOrEditingComment}
            setCommentBeingEdited={setCommentBeingEdited}
            userWantsToEditThisComment={userWantsToEditThisComment}
            users={users}
            uid={user.uid}
            parentComment={comment}
            sessionId={sessionId}
          />
        </div>
        <div
          style={{ display: userWantsToReplyToThisComment ? 'block' : 'none' }}>
          <EnterYourComment
            key={comment.id}
            handlePostComment={handlePostComment}
            postingOrEditingComment={postingOrEditingComment}
            userWantsToReplyToThisComment={userWantsToReplyToThisComment}
            idOfCommentBeingRepliedTo={idOfCommentBeingRepliedTo}
            setIdOfCommentBeingRepliedTo={setIdOfCommentBeingRepliedTo}
            users={users}
            uid={user.uid}
            parentComment={comment}
            sessionId={sessionId}
          />
        </div>
        {comments
          .filter((_comment) => _comment.parentCommentId === comment.id)
          .map((_comment, _i, _arr) => (
            <Reply
              ref={(el) => {
                if (_i === _arr.length - 1) {
                  lastReplyRef.current = el;
                }
                replyRefs.current[_i] = el;
              }}
              i={_i}
              arr={_arr}
              commentBeingEdited={commentBeingEdited}
              comments={comments}
              comment={_comment}
              handleEditComment={handleEditComment}
              handlePostComment={handlePostComment}
              idOfCommentBeingRepliedTo={idOfCommentBeingRepliedTo}
              key={_comment.id}
              level={level + 1}
              parentCommentId={_comment.parentCommentId}
              postingOrEditingComment={postingOrEditingComment}
              setCommentBeingEdited={setCommentBeingEdited}
              setIdOfCommentBeingRepliedTo={setIdOfCommentBeingRepliedTo}
              setQuestionIdQueryParam={setQuestionIdQueryParam}
              user={user}
              users={users}
              sessionId={sessionId}
              commentIdQueryParam={commentIdQueryParam}
              handleDeleteComment={handleDeleteComment}
              deletingComment={deletingComment}
              handleDialog={handleDialog}
              onlineUsers={onlineUsers}
            />
          ))}
        {level === 0 && <Divider />}
        <Dialog
          ref={deleteCommentDialogRef}
          isVisible={showDeleteDialog}
          onDismiss={handleDismissDeleteCommentDialog}
          contentStyle={{ maxWidth: '454px' }}>
          <DeleteCommentDialog
            onConfirm={() => {
              if (userWantsToReplyToThisComment) {
                setIdOfCommentBeingRepliedTo(null);
              }
              if (userWantsToEditThisComment) {
                setCommentBeingEdited(null);
              }
              handleDeleteComment(comment);
              setShowDeleteCommentDialog(false);
            }}
            onDismiss={handleDismissDeleteCommentDialog}
            deleting={deletingComment}
          />
        </Dialog>
      </Container>
    );
  }
);

const Divider = styled.div`
  background-color: var(--light-grey);
  height: 0.063rem;
  left: -1.5rem;
  margin-top: 18px;
  position: relative;
  width: calc(100% + 2.25rem);

  @media (min-width: 768px) {
    margin-top: 10px;
    left: -3.9rem;
    width: calc(100% + 1.9rem);
  }
`;

const Container = styled.div`
  background-color: #fff;
  padding: ${({ $lastComment, $userWantsToEditThisComment, $isReply }) =>
    $lastComment && !$userWantsToEditThisComment && !$isReply
      ? '33px 22px 16px 35px'
      : $lastComment && $userWantsToEditThisComment && !$isReply
        ? '33px 22px 9px 35px'
        : $isReply
          ? '23px 0px 0px 23px'
          : '33px 22px 0px 35px'};

  @media (min-width: 768px) {
    padding: ${({ $lastComment, $userWantsToEditThisComment, $isReply }) =>
      $lastComment && !$userWantsToEditThisComment && !$isReply
        ? '33px 22px 16px 116px'
        : $lastComment && $userWantsToEditThisComment && !$isReply
          ? '33px 22px 9px 116px'
          : $isReply
            ? '20px 0px 0px 56px'
            : '33px 22px 0px 116px'};
  }

  @media (max-width: 499px) {
    ${({ $level }) => {
      if ($level > 2) {
        return css`
          padding-left: 0;

          .connector-svg {
            display: none;
          }
        `;
      }
      if ($level > 1) {
        return css`
          .connector-svg {
            display: none;
          }
        `;
      }
    }}
  }

  @media (min-width: 500px) and (max-width: 569px) {
    ${({ $level }) => {
      if ($level > 3) {
        return css`
          padding-left: 0;

          .connector-svg {
            display: none;
          }
        `;
      }
      if ($level > 2) {
        return css`
          .connector-svg {
            display: none;
          }
        `;
      }
    }}
  }

  @media (min-width: 570px) and (max-width: 639px) {
    ${({ $level }) => {
      if ($level > 4) {
        return css`
          padding-left: 0;

          .connector-svg {
            display: none;
          }
        `;
      }
      if ($level > 3) {
        return css`
          .connector-svg {
            display: none;
          }
        `;
      }
    }}
  }

  @media (min-width: 640px) and (max-width: 709px) {
    ${({ $level }) => {
      if ($level > 5) {
        return css`
          padding-left: 0;

          .connector-svg {
            display: none;
          }
        `;
      }
      if ($level > 4) {
        return css`
          .connector-svg {
            display: none;
          }
        `;
      }
    }}
  }

  @media (min-width: 710px) and (max-width: 767px) {
    ${({ $level }) => {
      if ($level > 6) {
        return css`
          padding-left: 0;

          .connector-svg {
            display: none;
          }
        `;
      }
      if ($level > 5) {
        return css`
          .connector-svg {
            display: none;
          }
        `;
      }
    }};
  }

  @media (min-width: 768px) {
    ${({ $level }) => {
      if ($level > 4) {
        return css`
          padding-left: 0;

          .connector-svg {
            display: none;
          }
        `;
      }
      if ($level > 3) {
        return css`
          .connector-svg {
            display: none;
          }
        `;
      }
    }};
  }

  ${Divider} {
    display: ${({ $lastComment }) => ($lastComment ? 'none' : 'block')};
  }

  .markup {
    font-family: 'Inter', sans-serif;
    font-size: 0.875rem;
    line-height: 120%;
    margin-bottom: 11px;

    p {
      margin: 0.75em 0 1em;
    }

    img {
      display: block;
      height: auto;
      max-width: 100%;
    }
  }
`;

const TopLine = styled.div`
  align-items: center;
  display: flex;
  gap: 11px;
  left: -31px;
  margin-bottom: 0.95rem;
  position: relative;
  white-space: nowrap;

  h4 {
    font-family: 'Poppins', sans-serif;
    font-size: 0.85rem;
    font-weight: 700;

    span {
      color: var(--blue);
      font-weight: 400;
    }
  }

  @media (min-width: 768px) {
    gap: 10px;
    left: -56px;
    margin-bottom: 0.025rem;

    h4 {
      font-size: 1rem;
    }
  }
`;

const Timestamp = styled.span`
  bottom: 0.01em;
  display: inline-block;
  font-size: 0.75rem;
  position: relative;

  @media (min-width: 768px) {
    font-size: 0.9rem;
  }
`;

const IsModerator = styled(Timestamp)`
  font-weight: 700 !important;
`;

const Buttons = styled.div`
  align-items: center;
  display: flex;

  button {
    font-family: 'Inter', sans-serif;
    font-size: 0.875rem;
    font-weight: 400;
    white-space: nowrap;
  }
`;

export default Comment;
